import { kopiere, kopiereGleich } from '../kopieren';
import { useEffect, useState, type ReactElement } from 'react';
import { api, type ProjectRole } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * **Zusammenarbeiten** -- vorher „Teilen“ (Sascha, 27. Oktober: „der Export/Teilen Modal ist so
 * verschachtelt und viel Text. Es ist einfach nicht automatisch schlüssig was
 * passiert. Hat Veronika automatisch Zugriff, wenn ich ihr den Link fürs Team
 * schicke?“ -- nein, hatte sie nicht, und genau daran scheiterte sie, 7.266).
 *
 * „Teilen kann auch zusammenarbeiten heißen“ (Sascha, gleicher Tag).
 *
 * Ein Blatt statt zweier: wer im Team ist, wer dazukommt, und die Links für
 * Externe. **Eine Person antippen heisst: sie ist dabei, und der Link liegt in
 * der Zwischenablage** -- ein Schritt, kein „Link kopieren“ hier und
 * „Zugriff geben“ ein Blatt weiter. Ein weitergeleiteter Link öffnet deshalb
 * weiterhin bei niemand anderem (Saschas Wahl: „Person wählen, fertig“).
 */

interface Member {
  id: string;
  name: string;
  email: string;
  role: ProjectRole;
}

interface Kandidat {
  id: string;
  name: string;
  email: string;
}

interface ShareLink {
  id: string;
  /** Null when the server can no longer read it back (SESSION_SECRET changed). */
  token: string | null;
  /** Für wen er ist. Leer heisst: der Gast nennt sich selbst (7.63). */
  name: string;
  role: 'editor' | 'viewer';
  expires_at: string | null;
  created_at: string;
}

/** So viele stehen ohne Suche da; der Rest findet sich über das Feld. */
const KANDIDATEN_SICHTBAR = 6;

const TITEL = { fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' } as const;

const ROLLE: Record<ProjectRole, string> = {
  owner: 'Eigentümerin',
  editor: 'Schneiden',
  viewer: 'Nur ansehen',
};

export function TeilenSheet({ onClose, closeLabel }: { onClose: () => void; closeLabel?: string }): ReactElement {
  const project = useEditor((s) => s.project);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const isOwner = role === 'owner';

  const [members, setMembers] = useState<Member[]>([]);
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([]);
  const [suche, setSuche] = useState('');
  /** Für wen der nächste Gast-Link ist. Leer bleibt erlaubt (7.63). */
  const [gastName, setGastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!project) return;
    try {
      const data = await api.members(project.id);
      setMembers(data.members);
      setLinks(data.shareLinks);
      if (role === 'owner') setKandidaten((await api.kandidaten(project.id)).kandidaten);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Wer dabei ist, liess sich nicht laden.');
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const s = suche.trim().toLowerCase();
  const passende = kandidaten.filter((k) => !s || k.name.toLowerCase().includes(s) || k.email.toLowerCase().includes(s));
  const istAdresse = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suche.trim());

  /**
   * Dazuholen und den Link mitgeben. Der Kopierversuch beginnt in der
   * Berührung (`kopiereGleich`) -- danach wäre die Geste vorbei, und Safari
   * lehnte ab.
   */
  const dazuholen = async (wer: { userId: string } | { email: string }, name: string) => {
    if (!project) return;
    setBusy(true);
    const anfrage = api.addMember(project.id, wer, 'editor');
    const kopiert = kopiereGleich(anfrage.then(() => project.permalink));
    try {
      await anfrage;
      const ok = await kopiert;
      setSuche('');
      await load();
      toast('success', ok ? `${name} ist dabei. Link kopiert -- einfach schicken.` : `${name} ist dabei.`);
    } catch (err) {
      toast(
        'error',
        err instanceof Error && /Kein Benutzer/.test(err.message)
          ? 'Für diese Adresse gibt es kein Konto. Ohne Konto: Link für Externe.'
          : err instanceof Error
            ? err.message
            : 'Dazuholen ging nicht.',
      );
    } finally {
      setBusy(false);
    }
  };

  const rolle = async (member: Member, next: ProjectRole) => {
    if (!project || next === member.role) return;
    setBusy(true);
    try {
      await api.addMember(project.id, { userId: member.id }, next);
      await load();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Rolle liess sich nicht ändern.');
    } finally {
      setBusy(false);
    }
  };

  const entfernen = async (member: Member) => {
    if (!project) return;
    setBusy(true);
    try {
      await api.removeMember(project.id, member.id);
      await load();
      toast('info', `${member.name} ist nicht mehr dabei.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Entfernen ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const gastLink = async (linkRole: 'editor' | 'viewer') => {
    if (!project) return;
    setBusy(true);
    const anfrage = api.createShareLink(project.id, linkRole, gastName.trim());
    const kopiert = kopiereGleich(anfrage.then((l) => l.url));
    try {
      const link = await anfrage;
      const ok = await kopiert;
      setGastName('');
      await load();
      const fuer = link.name ? ` für ${link.name}` : '';
      // Nur „kopiert“, wenn es stimmt.
      if (ok) toast('success', `Link${fuer} kopiert.`);
      else toast('info', `Link${fuer} angelegt. Kopieren ging nicht von selbst -- unten auf „Kopieren“ tippen.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Link liess sich nicht anlegen.');
    } finally {
      setBusy(false);
    }
  };

  const widerrufen = async (linkId: string) => {
    if (!project) return;
    setBusy(true);
    try {
      await api.revokeShareLink(project.id, linkId);
      await load();
      toast('info', 'Link widerrufen.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Widerrufen ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const kopiert = (ok: boolean, was = 'Kopiert.') =>
    ok ? toast('success', was) : toast('error', 'Kopieren ging nicht -- den Link bitte von Hand markieren.');

  return (
    <Sheet title="Zusammenarbeiten" onClose={onClose} closeLabel={closeLabel}>
      <div data-teilen>
        <h3 style={{ ...TITEL, marginTop: 0 }}>Team</h3>
        {/* Ob die Liste schon da ist, steht am Kasten: der Browser-Durchgang
            zählt hier Zeilen, und eine Zahl vor der Antwort ist keine (7.78). */}
        <div className="list" data-zugriffe={loaded ? 'da' : 'laedt'}>
          {!loaded ? <p className="hint">Lade…</p> : null}
          {members.map((member) => (
            <div className="row" key={member.id} data-mitglied={member.id}>
              <div className="grow">
                <div className="title">{member.name || member.email}</div>
                {isOwner && member.role !== 'owner' ? (
                  <div className="chips" style={{ marginTop: 6 }}>
                    {(['editor', 'viewer'] as const).map((r) => (
                      <button
                        key={r}
                        className={`chip ${member.role === r ? 'active' : ''}`}
                        onClick={() => void rolle(member, r)}
                        disabled={busy}
                      >
                        {ROLLE[r]}
                      </button>
                    ))}
                    <button className="chip" onClick={() => void entfernen(member)} disabled={busy}>
                      Entfernen
                    </button>
                  </div>
                ) : null}
              </div>
              {member.role === 'owner' || !isOwner ? <span className="badge">{ROLLE[member.role]}</span> : null}
            </div>
          ))}
        </div>

        {isOwner ? (
          <form
            style={{ marginTop: 12 }}
            data-einladen
            onSubmit={(e) => {
              e.preventDefault();
              if (istAdresse && passende.length === 0) void dazuholen({ email: suche.trim() }, suche.trim());
            }}
          >
            <input
              type="search"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Dazuholen: Name oder E-Mail"
              aria-label="Person dazuholen"
              autoCapitalize="none"
              autoComplete="off"
              style={{ width: '100%' }}
              data-einladen-suche
            />
            <div className="list kandidaten" data-kandidaten style={{ marginTop: 6 }}>
              {passende.slice(0, s ? 20 : KANDIDATEN_SICHTBAR).map((k) => (
                <button
                  type="button"
                  key={k.id}
                  className="row kandidat"
                  onClick={() => void dazuholen({ userId: k.id }, k.name || k.email)}
                  disabled={busy}
                  data-kandidat={k.id}
                >
                  <div className="grow">
                    <div className="title">{k.name || k.email}</div>
                    {k.name ? <div className="sub">{k.email}</div> : null}
                  </div>
                  <span className="badge">+ dazu</span>
                </button>
              ))}
              {istAdresse && passende.length === 0 ? (
                <button type="submit" className="row kandidat" disabled={busy} data-einladen-adresse>
                  <div className="grow title">{suche.trim()}</div>
                  <span className="badge">+ dazu</span>
                </button>
              ) : null}
              {!s && passende.length > KANDIDATEN_SICHTBAR ? (
                <p className="hint" style={{ margin: '4px 0' }}>und {passende.length - KANDIDATEN_SICHTBAR} weitere -- oben suchen.</p>
              ) : null}
            </div>
            {s && passende.length === 0 && !istAdresse ? (
              <p className="hint" style={{ margin: '4px 0' }}>Kein Konto gefunden. Ohne Konto: Link für Externe, unten.</p>
            ) : null}
          </form>
        ) : (
          <p className="hint">Dazuholen kann, wer das Projekt angelegt hat.</p>
        )}

        <button
          style={{ width: '100%', marginTop: 10 }}
          data-teilen-kopieren
          onClick={() => void kopiere(project?.permalink ?? '').then((ok) => kopiert(ok, 'Kopiert. Öffnet für alle, die oben stehen.'))}
        >
          Link fürs Team kopieren
        </button>

        <h3 style={TITEL}>Externe, ohne Konto</h3>
        {links.length > 0 ? (
          <div className="list" data-gastlinks>
            {links.map((link) => (
              <div className="row" key={link.id}>
                <div className="grow">
                  {/* Der Name zuerst: „Für Anna“ sagt mehr als „darf zusehen“ (7.63). */}
                  <div className="title" data-linkname>
                    {link.name ? `Für ${link.name}` : 'Ohne Namen'}
                  </div>
                  <div className="sub">
                    {link.role === 'editor' ? 'darf mitschneiden' : 'darf zusehen'} · seit {new Date(link.created_at).toLocaleDateString('de-DE')}
                  </div>
                </div>
                {link.token ? (
                  <button
                    className="small"
                    data-link-kopieren
                    onClick={() => void kopiere(`${project?.permalink}?share=${link.token}`).then((ok) => kopiert(ok))}
                  >
                    Kopieren
                  </button>
                ) : (
                  <span className="hint" title="Der Link gilt weiter, lässt sich aber nicht mehr anzeigen.">
                    nicht kopierbar
                  </span>
                )}
                {isOwner ? (
                  <button className="small" onClick={() => void widerrufen(link.id)} disabled={busy}>
                    Widerrufen
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {isOwner ? (
          <>
            <input
              value={gastName}
              maxLength={80}
              placeholder="Für wen? (z. B. Anna)"
              onChange={(e) => setGastName(e.target.value)}
              aria-label="Name des Gastes"
              data-gastname
              style={{ width: '100%', marginTop: links.length ? 10 : 0 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="primary" style={{ flex: 1 }} onClick={() => void gastLink('editor')} disabled={busy} data-teilen-gast="editor">
                Link zum Mitschneiden
              </button>
              <button style={{ flex: 1 }} onClick={() => void gastLink('viewer')} disabled={busy} data-teilen-gast="viewer">
                Link zum Ansehen
              </button>
            </div>
          </>
        ) : links.length === 0 ? (
          <p className="hint" style={{ marginTop: 0 }}>Noch keiner.</p>
        ) : null}
      </div>
    </Sheet>
  );
}
